// shared/company-export.js — leaving without losing anything (feature sketch 6).
//
// Two things a person reads around a subscription ending, said once here so
// the web, the demo and the iOS app (through shared/ios-facade.js) say them
// the same way:
//
//   - the promise, where the subscription is managed, and the banner the
//     lists show once a trial or a subscription has ended;
//   - "Export everything": what one zip file holds, and where an export the
//     company asked for has got to.
//
// The export itself is built on the server (server/company-export.js); what
// is here is only what a screen says about it.
//
// Pure: imports only from shared/, no DOM, no node builtins.

import { formatAttachmentRoom } from "./email-attachments.js";
import { formatDocumentDate } from "./document-format.js";
import { EXPORT_LINK_DAYS } from "./product-facts.js";
import { FREE_ACCESS_COPY, freeAccessStatus } from "./free-access.js";

// How long the emailed link works, and so how long the file is kept: a
// product figure, so the marketing copy quotes it with a marker.
export { EXPORT_LINK_DAYS };

export const LEAVING_COPY = Object.freeze({
  heading: "If you leave",
  promise: "Everything stays here to read and export. Nothing is deleted when a subscription ends.",
  exportTitle: "Export everything",
  prepare: "Prepare export",
  prepareAgain: "Prepare a new export",
  download: "Download export",
  note: `One zip file. We’ll email you a link when it’s ready, usually within a few minutes. The link works for ${EXPORT_LINK_DAYS} days.`,
  // An export somebody else asked for is still being prepared: one at a time.
  oneAtATime: "An export is already being prepared. We’ll email a link to whoever asked for it when it’s ready.",
  couldNotStart: "Couldn’t start your export. Please try again.",
  couldNotDownload: "Couldn’t download your export. Please try again.",
  // The account deletion flow, before anything goes.
  beforeDeleting: "Export everything first to keep a copy of your clients, invoices, quotes, credit notes, payments, and every sent document.",
  exportFirst: "Export everything first",
  // An export goes with the company it was made from, and so with an
  // account that owns it. While one is being prepared the delete is refused
  // (POST /profile/delete), or the email promised would never come; once it
  // is ready, the dialog says the link stops working.
  deletePreparingTitle: "Your export is still being prepared",
  deletePreparing: "Deleting your account now would delete it too. Download it once it’s ready, then delete your account.",
  deleteRefusedPreparing: "Your export is still being prepared. Download it once it’s ready, then delete your account.",
  deleteLosesExport: "Deleting your account deletes your export too, and the link we emailed stops working. Download it first.",
  // Only the subscriber can cancel an App Store subscription (ADR-022), so
  // deleting a company it pays for doesn't stop it. Said before it goes.
  deleteCompanyAppStore: "This company’s Sendinvo subscription is paid through the App Store, and deleting the company doesn’t cancel it. Cancel it in your iPhone’s subscription settings first, or Apple will keep charging you.",
  openAppStoreSubscriptions: "Open App Store subscriptions"
});

// The statuses a subscription row can hold that mean it is over: what
// server/routes/billing.js calls `ended` when Stripe says so.
const ENDED_STATUSES = new Set(["canceled", "incomplete_expired"]);
const ACTIVE_STATUSES = new Set(["active", "trialing"]);
// A subscription that exists and isn't paying: its payment is what needs
// doing. `incomplete` is one whose first payment never went through, and
// `paused` one Stripe stopped for want of a payment method.
const PAYMENT_STATUSES = new Set(["past_due", "unpaid", "incomplete", "paused"]);

/**
 * Which of four situations a company that may not send is in, for the
 * words, from its subscription's status and who bills it. The one reading
 * every surface tells the story by (30 October 2026): the API's 402
 * (`subscriptionRequiredMessage`), the sends nobody pressed
 * (`automaticSendRefusal`, server/send-entitlement.js), the web's paywall for
 * a Member (`subscriptionLapsedForOthers`), and Subscription as an owner and
 * as a Member reads it (`webBillingWords`, `subscriptionReadOnly`). They used
 * to read the status each their own way, so `incomplete_expired` was "Your
 * subscription has ended" on the banner and "Your free trial has ended" on
 * Send, and `incomplete` and `paused` read "Free trial" on Subscription
 * beside a refusal saying it had ended.
 *
 *   - "appStorePayment": an App Store payment that needs attention;
 *   - "payment": a subscription whose payment needs attention (`PAYMENT_STATUSES`);
 *   - "ended": a subscription that is over (`ENDED_STATUSES`), the same test
 *     the lists' banner ends on (`subscriptionEnded`);
 *   - "trial": no subscription, so the free trial is what ran out.
 *
 * @returns {"appStorePayment" | "payment" | "ended" | "trial"}
 */
export function subscriptionSituation(status, provider = null) {
  const s = String(status || "none");
  if (provider === "apple" && s === "past_due") return "appStorePayment";
  if (PAYMENT_STATUSES.has(s)) return "payment";
  if (ENDED_STATUSES.has(s)) return "ended";
  return "trial";
}

/**
 * Whether the company's trial or subscription has ended: `"trial"`,
 * `"subscription"`, or null while it hasn't (or nothing is known).
 *
 * `subscription` is the block `GET /auth/me` and every `/sync/pull` page carry:
 * `{ subscribed, status, inTrial, trialEndsAt }`, built on the server by
 * `subscriptionSummaryFrom` (server/send-entitlement.js). Nothing at all (an
 * older server, the demo) is not an ending. A payment that needs attention
 * (`past_due`, `unpaid`) hasn't ended either: sending says what to do about
 * that when it is refused, and Stripe or the App Store is already asking the
 * payer.
 *
 * It is the one test for three things, so they can't disagree: the lists'
 * banner (`subscriptionEndedNotice`), the payment reminders the hourly tick
 * stops sending (server/reminders.js), and the invoice's Reminders section
 * saying they are on hold (`remindersOnHoldLine`). Decided 10 October 2026,
 * Andy leaving it to judgement (ADR-032).
 *
 * A trial is worked out against `now` as well as read off `inTrial`, so a
 * phone that last heard from the server yesterday still knows a trial that
 * ended this morning has ended.
 *
 * @returns {"trial" | "subscription" | null}
 */
export function subscriptionEnded(subscription, now = Date.now()) {
  if (!subscription || typeof subscription !== "object") return null;
  const status = String(subscription.status || "none");
  if (subscription.subscribed === true || ACTIVE_STATUSES.has(status)) return null;
  if (ENDED_STATUSES.has(status)) return "subscription";
  if (status !== "none") return null;
  // The trial is the company's only while it has never subscribed at all
  // (getTrialInfo in server/routes/billing.js), which is what "none" is.
  const ends = Date.parse(String(subscription.trialEndsAt || ""));
  const stillInTrial = subscription.inTrial === true && (!Number.isFinite(ends) || ends > Number(now));
  return stillInTrial ? null : "trial";
}

/**
 * The banner the lists show once a trial or a subscription has ended, or null.
 *
 * `canSubscribe` is whether this person can do anything about it (the
 * owner's and admins' `billing.view`). They get `action`, the banner's one
 * button, which subscribes there and then: "Subscribe", or "Subscribe again"
 * once a subscription has ended, as the sentence above it says. Everybody
 * else is told who can, and gets no button.
 *
 * It names INVOICES AND REMINDERS because those are what stop: sending an
 * invoice (`sendEntitlement` in server/send-entitlement.js: the invoice send,
 * resend and schedule routes, starting, resuming, Send now and Retry on a
 * recurring schedule, and the booked sends and recurring runs that go on
 * their own) and the payment reminders about invoices already sent. A quote
 * or a credit note still goes out, and so do quote follow-ups.
 *
 * It doesn't say that everything is still there to read and export (#1001):
 * that is for somebody looking for it, in Profile's If you leave and Export
 * everything (`exportWords`), not for the moment a trial ends.
 *
 * Shown on Invoices, Quotes, Clients and Recurring. On Recurring
 * (`recurring: true`) the sentence is about the schedules, because a
 * schedule active when the trial or subscription ended still reads Active
 * with a next date until its run fails (#967). It doesn't say "paused":
 * Paused is something a person chose, undone with Resume, and a run that has
 * failed waits for Retry.
 *
 * @returns {{ title: string, body: string, action: string | null } | null}
 */
export function subscriptionEndedNotice({ subscription = null, canSubscribe = true, now = Date.now(), recurring = false } = {}) {
  const ended = subscriptionEnded(subscription, now);
  if (!ended) return null;

  const title = ended === "subscription" ? "Your subscription has ended" : "Your free trial has ended";
  const again = ended === "subscription" ? "subscribe again" : "subscribe";
  let body;
  if (recurring) {
    body = `Recurring invoices won’t go out until ${canSubscribe ? `you ${again}` : `an owner or admin ${subscribes(ended)}`}.`;
  } else {
    body = canSubscribe
      ? `${capitalise(again)} to send invoices and reminders.`
      : `An owner or admin can ${again} to send invoices and reminders.`;
  }
  // The button is "Subscribe" either way (Andy, 12 Oct 2026): the sentence
  // above it already says "again" where it applies.
  return { title, body, action: canSubscribe ? "Subscribe" : null };
}

/**
 * What an invoice's Reminders section says in place of "Next: …" once the
 * trial or subscription has ended, or null. The reminders stay Active, with
 * their buttons: nothing about the invoice changed, and the next one due
 * after subscribing again goes as it was going to. Those that come due in
 * between are skipped on the hour, never sent late.
 *
 *   "On hold until you subscribe again."
 *   "On hold until an owner or admin subscribes." (a Member, after a trial)
 *
 * @returns {string | null}
 */
export function remindersOnHoldLine({ subscription = null, canSubscribe = true, now = Date.now() } = {}) {
  const ended = subscriptionEnded(subscription, now);
  if (!ended) return null;
  return canSubscribe
    ? `On hold until you ${ended === "subscription" ? "subscribe again" : "subscribe"}.`
    : `On hold until an owner or admin ${subscribes(ended)}.`;
}

// ── Subscription, read only (30 October 2026) ───────────────────────────────
//
// A Member or a Viewer may need to know why invoices have stopped going out,
// so Subscription is theirs to read too: the plan, where it stands, and who
// can change it, with no Subscribe or Manage button. Owners and admins
// (`billing.view`) get the page with its buttons, and a Field person never
// reaches it (ADR-041). Read from the block /auth/me and every /sync/pull
// page carry, never /billing/status, which is billing.view's.

export const SUBSCRIPTION_READ_ONLY_COPY = Object.freeze({
  planLabel: FREE_ACCESS_COPY.planLabel,
  plan: FREE_ACCESS_COPY.plan,
  statusLabel: FREE_ACCESS_COPY.statusLabel,
  active: "Active",
  payment: "Payment needs attention",
  ended: "Ended",
  trialEnded: "Free trial ended",
  trial: "Free trial",
  stoppedTrial: "Invoices and payment reminders won’t go out until the company subscribes.",
  stoppedSubscription: "Invoices and payment reminders won’t go out until the company subscribes again.",
  stoppedPayment: "Invoices won’t go out until the payment is sorted out."
});

/** "12 November 2026", the London day an instant falls on, or "". */
function londonDay(instant) {
  const ms = Date.parse(String(instant || ""));
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" });
}

/**
 * Who can change the subscription, named for somebody who can't: the owner
 * by the name the team shows them, and admins, who can too.
 *
 *   "Only Andy Shaw, the owner, can change the subscription."
 *   "Only Andy Shaw, the owner, or an admin can change the subscription."
 *   "Only the owner or an admin can change the subscription." (the team unknown)
 */
export function subscriptionChangedBy({ ownerName = "", hasAdmins = false } = {}) {
  const name = String(ownerName || "").trim();
  // Nobody known yet (the team hasn't answered): admins may be among them.
  if (!name) return "Only the owner or an admin can change the subscription.";
  return `Only ${name}, the owner, ${hasAdmins ? "or an admin " : ""}can change the subscription.`;
}

/**
 * Subscription as a Member or a Viewer reads it: `{ planLabel, plan,
 * statusLabel, status, stopped, who }`. `status` is where it stands ("Free
 * trial until 12 November 2026", "Active", "Free until 1 March 2027",
 * "Payment needs attention", "Ended", "Free trial ended"); `stopped` is what
 * has stopped going out because of it, or "" while nothing has; `who` is
 * `subscriptionChangedBy`'s.
 */
export function subscriptionReadOnly({ subscription = null, ownerName = "", hasAdmins = false, now = Date.now() } = {}) {
  const copy = SUBSCRIPTION_READ_ONLY_COPY;
  const sub = subscription && typeof subscription === "object" ? subscription : {};
  const status = String(sub.status || "none");
  const ended = subscriptionEnded(sub, now);
  let shown;
  let stopped = "";
  if (sub.freeAccess) shown = freeAccessStatus(sub.freeAccess);
  else if (subscriptionSituation(status) === "payment") {
    shown = copy.payment;
    stopped = copy.stoppedPayment;
  } else if (ended === "subscription") {
    shown = copy.ended;
    stopped = copy.stoppedSubscription;
  } else if (ended === "trial") {
    shown = copy.trialEnded;
    stopped = copy.stoppedTrial;
  } else if (sub.subscribed === true || ACTIVE_STATUSES.has(status)) shown = copy.active;
  else {
    const day = londonDay(sub.trialEndsAt);
    shown = day ? `${copy.trial} until ${day}` : copy.trial;
  }
  return {
    planLabel: copy.planLabel,
    plan: copy.plan,
    statusLabel: copy.statusLabel,
    status: shown,
    stopped,
    who: subscriptionChangedBy({ ownerName, hasAdmins })
  };
}

/**
 * What Subscription says under a web subscription set to end at its period's
 * end (cancelled in Stripe's portal), where it used to say "Renews" on the
 * day it ends (30 October 2026). Renewing is the owner's (`billing.cancel`
 * is theirs, and Stripe's portal gives an admin only the card), so anybody
 * else is told who can.
 *
 *   "Your subscription ends on 11 November 2026, so you won’t be charged
 *    again. To keep sending invoices after that, renew it from Manage billing."
 */
export function subscriptionEndingNote(periodEnd, { canRenew = true } = {}) {
  const day = londonDay(periodEnd);
  const ends = day ? `Your subscription ends on ${day}, so you won’t be charged again.` : "Your subscription has been cancelled, so you won’t be charged again.";
  return `${ends} ${canRenew ? "To keep sending invoices after that, renew it from Manage billing." : "The owner can renew it from Manage billing to keep sending invoices after that."}`;
}

function subscribes(ended) {
  return ended === "subscription" ? "subscribes again" : "subscribes";
}

/**
 * What one export holds, as the screen lists it before anybody asks for one.
 * `documents` is how many sent documents it will carry as PDFs, which is the
 * only figure the list shows: the rest are one spreadsheet each. `photos`,
 * the job photos it will carry, gets a row of its own once there are any, and
 * so does `files`, the files kept on jobs (#1082), and `voiceNotes` (#1080).
 * `jobs` and `savedItems` (30 October 2026) each add a spreadsheet row once
 * the company has any; a caller that doesn't pass them (the iPhone, which
 * passes `documents` alone) gets the rows it had.
 *
 * @param {{ documents?: number, photos?: number, files?: number, voiceNotes?: number, jobs?: number, savedItems?: number }} counts
 * @returns {Array<{ label: string, value: string }>}
 */
export function exportContentsRows(counts = {}) {
  const documents = Math.max(0, Math.round(Number(counts?.documents) || 0));
  const photos = Math.max(0, Math.round(Number(counts?.photos) || 0));
  const files = Math.max(0, Math.round(Number(counts?.files) || 0));
  const voiceNotes = Math.max(0, Math.round(Number(counts?.voiceNotes) || 0));
  const jobs = Math.max(0, Math.round(Number(counts?.jobs) || 0));
  const savedItems = Math.max(0, Math.round(Number(counts?.savedItems) || 0));
  return [
    { label: "Clients", value: "Spreadsheet" },
    { label: "Invoices, quotes, and credit notes", value: "Spreadsheets" },
    { label: "Payments", value: "Spreadsheet" },
    { label: "Recurring schedules", value: "Spreadsheet" },
    ...(jobs > 0 ? [{ label: "Jobs and visits", value: "Spreadsheets" }] : []),
    ...(savedItems > 0 ? [{ label: "Saved items", value: "Spreadsheet" }] : []),
    { label: "Every sent document", value: documents === 0 ? "None yet" : pdfCount(documents) },
    ...(photos > 0 ? [{ label: "Job photos", value: photos === 1 ? "1 photo" : `${photos} photos` }] : []),
    ...(files > 0 ? [{ label: "Job files", value: files === 1 ? "1 file" : `${files} files` }] : []),
    ...(voiceNotes > 0 ? [{ label: "Voice notes", value: voiceNotes === 1 ? "1 voice note" : `${voiceNotes} voice notes` }] : [])
  ];
}

/** "1 PDF", "412 PDFs". */
export function pdfCount(n) {
  const count = Math.max(0, Math.round(Number(n) || 0));
  return `${count} ${count === 1 ? "PDF" : "PDFs"}`;
}

/**
 * Where an export has got to, as the screen says it, or null when there is
 * none to talk about. `exportRow` is the API's shape (`GET /company/export`):
 *
 *   { status: "preparing" | "ready" | "failed" | "expired",
 *     requestedByMe, requestedByName, expiresAt, sizeBytes,
 *     documents, renderedDocuments, missingDocuments, failure }
 *
 * @returns {{ status: string, title: string, detail: string } | null}
 */
export function exportStatusWords(exportRow) {
  if (!exportRow || typeof exportRow !== "object") return null;
  const status = String(exportRow.status || "");
  if (status === "preparing") {
    const name = String(exportRow.requestedByName || "").trim();
    if (exportRow.requestedByMe === true || !name) {
      return {
        status,
        title: "Preparing your export",
        detail: exportRow.requestedByMe === true
          ? "We’ll email you a link when it’s ready, usually within a few minutes."
          : "We’ll email a link to whoever asked for it when it’s ready."
      };
    }
    return {
      status,
      title: "Preparing an export",
      detail: `We’ll email ${name} a link when it’s ready, usually within a few minutes.`
    };
  }
  if (status === "ready") {
    const documents = Math.max(0, Math.round(Number(exportRow.documents) || 0));
    const size = formatAttachmentRoom(Number(exportRow.sizeBytes) || 0);
    const until = exportRow.expiresAt ? formatDocumentDate(exportRow.expiresAt) : "";
    const lines = [
      documents > 0 ? `${size}, with ${pdfCount(documents)}.` : `${size}.`,
      until
        ? `It can be downloaded until ${until}. After that it’s deleted, so an old email can’t be used to download your data.`
        : `It can be downloaded for ${EXPORT_LINK_DAYS} days. After that it’s deleted, so an old email can’t be used to download your data.`
    ];
    const rendered = Math.max(0, Math.round(Number(exportRow.renderedDocuments) || 0));
    if (rendered > 0) {
      lines.push(`${rendered === 1 ? "1 PDF was" : `${rendered} PDFs were`} made just now, because no copy was kept when ${rendered === 1 ? "it was" : "they were"} sent. “Read me” in the file lists ${rendered === 1 ? "it" : "them"}.`);
    }
    const missing = Math.max(0, Math.round(Number(exportRow.missingDocuments) || 0));
    if (missing > 0) {
      lines.push(`${missing === 1 ? "1 document" : `${missing} documents`} couldn’t be made into a PDF. Their figures are in the spreadsheets, and “Read me” lists ${missing === 1 ? "it" : "them"}.`);
    }
    return { status, title: "Your export is ready", detail: lines.join(" ") };
  }
  if (status === "failed") {
    const failure = String(exportRow.failure || "").trim();
    return { status, title: "Couldn’t prepare your export", detail: failure || "Please try again." };
  }
  if (status === "expired") {
    return { status, title: "Your last export has expired", detail: "Prepare a new one to download everything again." };
  }
  return null;
}

/**
 * Everything the export screen says, in one answer: the facade's
 * `exportWords`, and what the web's drawer draws from.
 */
export function exportWords({ counts = {}, exportRow = null } = {}) {
  return {
    ...LEAVING_COPY,
    linkDays: EXPORT_LINK_DAYS,
    rows: exportContentsRows(counts),
    status: exportStatusWords(exportRow)
  };
}

function capitalise(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}
